import React, { useEffect } from 'react';
import { XMarkIcon, ArrowDownTrayIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { useCallHistory } from './useCallHistory';
import { CallHistorySummaryCards } from './CallHistorySummaryCards';
import { CallHistoryFilterBar } from './CallHistoryFilterBar';
import { CallHistoryTable } from './CallHistoryTable';
import { CallHistoryPagination } from './CallHistoryPagination';

/**
 * Modal principal de Historial de Llamadas (Modo Claro)
 * Rediseñado con fidelidad a Stitch AI, Clean Code y arquitectura atómica modular.
 */
export function CallHistoryModal({
  isOpen,
  onClose,
  extension,
  onSelectClient,
  onSelectPhone,
  canCall = false,
}) {
  const {
    periodo,
    fechaDesde,
    fechaHasta,
    orden,
    direccion,
    estadoFiltro,
    pagina,
    totalPaginas,
    total,
    limite,
    searchTerm,
    setSearchTerm,
    data,
    rawCount,
    resumen,
    loading,
    error,
    setPagina,
    cambiarPeriodo,
    cambiarRangoFechas,
    cambiarLimite,
    cambiarOrden,
    cambiarEstadoFiltro,
    exportarAExcel,
    refetch,
  } = useCallHistory({ extension, isOpen });

  // Cerrar al pulsar Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/60 backdrop-blur-xs animate-in fade-in duration-200">
      {/* Contenedor del Modal */}
      <div
        className="bg-white border border-slate-300 w-full max-w-6xl max-h-[94vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden text-slate-800 animate-in zoom-in-95 duration-200"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-historial-title"
      >
        {/* 1. Cabecera Deep Slate con Insignia Dinámica y Botón Exportar a Excel */}
        <div className="bg-slate-900 text-white px-5 sm:px-6 py-3.5 sm:py-4 flex flex-wrap items-center justify-between gap-3.5 border-b border-slate-800 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-md shrink-0">
              <PhoneIcon className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2.5">
                <h2
                  id="modal-historial-title"
                  className="text-base sm:text-lg font-black tracking-tight text-white"
                >
                  Historial de Llamadas
                </h2>
                {canCall ? (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    En Vivo
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-slate-800 text-slate-300 border border-slate-700 select-none"
                    title="Llamadas habilitadas dentro del detalle de cobranza de un cliente"
                  >
                    <span>🔒</span>
                    Modo Consulta
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-400">
                Registro operacional auditado de telefonía y cobranza judicial
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 sm:gap-3">
            {/* Indicador de Operador / Extensión */}
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/10 border border-white/15 text-white text-xs font-medium">
              <span className="text-indigo-300 font-bold">🎧</span>
              <span className="font-semibold">EXT {extension || 'N/A'}</span>
            </div>

            {/* Botón Verde Exportar a Excel */}
            <button
              type="button"
              onClick={exportarAExcel}
              className="bg-emerald-600 hover:bg-emerald-500 active:scale-95 text-white text-xs font-bold px-3.5 py-2 rounded-xl flex items-center gap-1.5 shadow-sm transition-all cursor-pointer"
              title="Descargar historial visible en formato CSV/Excel con codificación UTF-8"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              <span>Exportar a Excel</span>
            </button>

            {/* Botón Cerrar Modal */}
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-white p-1.5 rounded-xl hover:bg-white/10 transition-colors cursor-pointer"
              title="Cerrar modal (Esc)"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 2. Cuerpo del Modal con Scroll */}
        <div className="p-4 sm:p-5 overflow-y-auto flex-1 bg-slate-100/90 space-y-4">
          {error && (
            <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold flex items-center justify-between shadow-xs">
              <div className="flex items-center gap-2">
                <span>⚠️</span>
                <span>{error}</span>
              </div>
              <button
                type="button"
                onClick={refetch}
                className="underline hover:text-rose-900 ml-2 cursor-pointer font-bold"
              >
                Reintentar
              </button>
            </div>
          )}

          {/* Tarjetas KPI de Resumen */}
          <CallHistorySummaryCards resumen={resumen} loading={loading} />

          {/* Barra de Filtros, Períodos y Búsqueda */}
          <CallHistoryFilterBar
            periodo={periodo}
            fechaDesde={fechaDesde}
            fechaHasta={fechaHasta}
            estadoFiltro={estadoFiltro}
            orden={orden}
            direccion={direccion}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            cambiarPeriodo={cambiarPeriodo}
            cambiarRangoFechas={cambiarRangoFechas}
            cambiarEstadoFiltro={cambiarEstadoFiltro}
            cambiarOrden={cambiarOrden}
            refetch={refetch}
            loading={loading}
          />

          {/* Tabla de Registros Operacionales de 10 Columnas */}
          <CallHistoryTable
            data={data}
            loading={loading}
            onSelectPhone={onSelectPhone}
            onSelectClient={onSelectClient}
            cambiarPeriodo={cambiarPeriodo}
            canCall={canCall}
          />

          {/* Barra de Paginación */}
          <CallHistoryPagination
            pagina={pagina}
            totalPaginas={totalPaginas}
            total={total}
            rawCount={rawCount}
            limite={limite}
            cambiarLimite={cambiarLimite}
            setPagina={setPagina}
            loading={loading}
          />
        </div>

        {/* 3. Pie del Modal con Nota Legal de Auditoría */}
        <div className="bg-white border-t border-slate-200/90 px-5 py-2.5 flex items-center justify-between text-[11px] text-slate-500 shrink-0">
          <div className="flex items-center gap-1.5">
            <span className="text-slate-400">🔒</span>
            <span>
              Llamadas registradas y auditadas conforme a políticas de calidad y cumplimiento de cobranzas.
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold transition cursor-pointer text-xs"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
